import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { unsubscribeNewsletter } from "@/lib/api/intake.functions";

export const Route = createFileRoute("/unsubscribe")({
  component: UnsubscribePage,
  head: () => ({
    meta: [
      { title: "Unsubscribe | Nkadimeng Danny Legal & Compliance" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
});

type State =
  | { kind: "loading" }
  | { kind: "ready" }
  | { kind: "already" }
  | { kind: "invalid" }
  | { kind: "submitting" }
  | { kind: "success" }
  | { kind: "error"; message: string };

function UnsubscribePage() {
  const [state, setState] = useState<State>({ kind: "loading" });
  const [token, setToken] = useState<string | null>(null);
  const [email, setEmail] = useState<string>("");
  const unsubscribeByEmail = useServerFn(unsubscribeNewsletter);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get("token");
    const e = params.get("email");
    if (t) {
      setToken(t);
      (async () => {
        try {
          const res = await fetch(
            `/email/unsubscribe?token=${encodeURIComponent(t)}`,
          );
          if (!res.ok) return setState({ kind: "invalid" });
          const data = (await res.json()) as {
            valid?: boolean;
            reason?: string;
          };
          if (data.valid) setState({ kind: "ready" });
          else if (data.reason === "already_unsubscribed")
            setState({ kind: "already" });
          else setState({ kind: "invalid" });
        } catch {
          setState({ kind: "error", message: "Could not reach the server." });
        }
      })();
      return;
    }
    // No token — manual unsubscribe form (POPIA easy-out). Prefill from ?email=.
    if (e) setEmail(e);
    setState({ kind: "ready" });
  }, []);

  async function confirm() {
    if (token) {
      setState({ kind: "submitting" });
      try {
        const res = await fetch("/email/unsubscribe", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token }),
        });
        const data = (await res.json()) as {
          success?: boolean;
          reason?: string;
          error?: string;
        };
        if (data.success) setState({ kind: "success" });
        else if (data.reason === "already_unsubscribed")
          setState({ kind: "already" });
        else
          setState({
            kind: "error",
            message: data.error ?? "Unsubscribe failed.",
          });
      } catch {
        setState({ kind: "error", message: "Network error." });
      }
      return;
    }
    // Email-only path
    if (!email) return;
    setState({ kind: "submitting" });
    try {
      await unsubscribeByEmail({ data: { email } });
      setState({ kind: "success" });
    } catch (err) {
      setState({
        kind: "error",
        message: err instanceof Error ? err.message : "Unsubscribe failed.",
      });
    }
  }

  return (
    <div className="mx-auto flex min-h-[60vh] max-w-xl flex-col items-center justify-center px-4 py-16 text-center">
      <p className="text-xs uppercase tracking-[0.3em] text-gold">
        Email preferences
      </p>
      <h1 className="mt-3 font-display text-3xl text-navy">Unsubscribe</h1>

      {state.kind === "loading" && (
        <p className="mt-6 text-sm text-muted-foreground">Checking your link…</p>
      )}

      {state.kind === "ready" && (
        <>
          <p className="mt-6 text-sm text-muted-foreground">
            Click below to stop receiving emails from Nkadimeng Danny Legal &
            Compliance Support. You can resubscribe at any time.
          </p>
          {!token && (
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="mt-6 w-full max-w-sm rounded-md border border-input bg-background px-3 py-2.5 text-sm"
            />
          )}
          <button
            onClick={confirm}
            disabled={!token && !email}
            className="mt-4 inline-flex items-center justify-center rounded-md bg-navy px-6 py-2.5 text-sm font-medium text-navy-foreground hover:bg-navy/90 disabled:opacity-60"
          >
            Confirm unsubscribe
          </button>
        </>
      )}

      {state.kind === "submitting" && (
        <p className="mt-6 text-sm text-muted-foreground">Processing…</p>
      )}

      {state.kind === "success" && (
        <p className="mt-6 text-sm text-muted-foreground">
          You've been unsubscribed. Sorry to see you go.
        </p>
      )}

      {state.kind === "already" && (
        <p className="mt-6 text-sm text-muted-foreground">
          This email is already unsubscribed. No further action needed.
        </p>
      )}

      {state.kind === "invalid" && (
        <p className="mt-6 text-sm text-muted-foreground">
          This unsubscribe link is invalid or has expired.
        </p>
      )}

      {state.kind === "error" && (
        <p className="mt-6 text-sm text-destructive">{state.message}</p>
      )}
    </div>
  );
}
